import { IdentityActiveSummary } from '../../queries'
import { ActivityEventType } from '../../../../accessControl/accessControlSlice'
import { ActivityLogGroupingInterval } from '../../../../../constants'
import dayjs from 'dayjs'

export const getActualTimeRangeFromGroupingInterval = (
  groupingInterval: ActivityLogGroupingInterval
): { start: string; end: string } => {
  const now = dayjs()
  let start

  switch (groupingInterval) {
    case ActivityLogGroupingInterval.hourly:
      start = now.subtract(24, 'hour')
      break
    case ActivityLogGroupingInterval.daily:
      start = now.subtract(7, 'day')
      break
    case ActivityLogGroupingInterval.weekly:
      start = now.subtract(30, 'days')
      break
    default:
      throw new Error(`Unsupported grouping interval: ${groupingInterval}`)
  }

  return {
    start: start.format('YYYY-MM-DDTHH:mm:ssZ'),
    end: now.format('YYYY-MM-DDTHH:mm:ssZ')
  }
}

export const getPredictedTime = (
  actualTimeRange: { start: string; end: string },
  groupingInterval: ActivityLogGroupingInterval
) => {
  const actualTimeRangeEnd = dayjs(actualTimeRange.end)

  switch (groupingInterval) {
    case ActivityLogGroupingInterval.hourly:
      return {
        start: actualTimeRangeEnd.format('YYYY-MM-DDTHH:mm:ssZ'),
        end: actualTimeRangeEnd.add(1, 'hour').format('YYYY-MM-DDTHH:mm:ssZ')
      }
    case ActivityLogGroupingInterval.daily:
      return {
        start: actualTimeRangeEnd.format('YYYY-MM-DDTHH:mm:ssZ'),
        end: actualTimeRangeEnd.add(1, 'day').format('YYYY-MM-DDTHH:mm:ssZ')
      }
    case ActivityLogGroupingInterval.weekly:
      return {
        start: actualTimeRangeEnd.format('YYYY-MM-DDTHH:mm:ssZ'),
        end: actualTimeRangeEnd.add(7, 'days').format('YYYY-MM-DDTHH:mm:ssZ')
      }
    default:
      throw new Error(`Unsupported grouping interval: ${groupingInterval}`)
  }
}

export const getAggregatedCountByFeature = ({
  widgetData = [],
  isSensitive
}: {
  widgetData?: IdentityActiveSummary[]
  isSensitive: boolean
}): Partial<Record<ActivityEventType, number>> => {
  const allFeatures: ActivityEventType[] = [
    ActivityEventType.all,
    ActivityEventType.read,
    ActivityEventType.write,
    ActivityEventType.download,
    ActivityEventType.delete,
    ActivityEventType.create
  ]

  const countField = isSensitive ? 'actualSensitiveActivityCount' : 'actualActivityCount'
  const initialCounts = Object.fromEntries(allFeatures.map((feature) => [feature, 0])) as Partial<
    Record<ActivityEventType, number>
  >

  if (widgetData.length === 0) {
    return initialCounts
  }

  return widgetData.reduce(
    (acc, entry) => {
      entry?.summaries?.forEach((summary) => {
        const { feature } = summary
        const count = summary[countField]

        if (typeof count === 'number') {
          acc[feature] = (acc[feature] || 0) + count
        }
      })
      return acc
    },
    { ...initialCounts }
  )
}

export type BarChartData = {
  name: string
  startTimestamp: string
  endTimestamp: string
  isPrediction?: boolean
} & Record<ActivityEventType, number>

export const transformWidgetData = ({
  widgetData,
  selectedFeature,
  isSensitive,
  groupingInterval
}: {
  widgetData: IdentityActiveSummary[]
  selectedFeature: ActivityEventType
  isSensitive: boolean
  groupingInterval: ActivityLogGroupingInterval
}): BarChartData[] => {
  if (!Array.isArray(widgetData)) return []

  const valueKey = isSensitive ? 'actualSensitiveActivityCount' : 'actualActivityCount'
  const predictedValueKey = isSensitive
    ? 'predictedSensitiveActivityCount'
    : 'predictedActivityCount'

  const processEntry = (entry: IdentityActiveSummary) => {
    const date = dayjs(entry.startTimestamp)
    let label: string

    switch (groupingInterval) {
      case ActivityLogGroupingInterval.hourly:
        label = date.format('ha')
        break
      case ActivityLogGroupingInterval.daily:
        label = date.format('ddd')
        break
      case ActivityLogGroupingInterval.weekly:
        label = date.format('D MMM')
        break
      default:
        label = date.format('ha')
    }

    const result = {
      name: label,
      timestamp: date.valueOf(),
      startTimestamp: entry.startTimestamp || '',
      endTimestamp: entry.endTimestamp || '',
      isPrediction: false
    }

    Object.values(ActivityEventType).forEach((feature) => {
      if (feature !== ActivityEventType.all) {
        result[feature] = 0
      }
    })

    entry.summaries?.forEach((summary) => {
      if (summary.feature in result) {
        result[summary.feature] += summary[valueKey] || 0

        if (
          typeof summary[predictedValueKey] === 'number' &&
          (summary[predictedValueKey] as number) > 0
        ) {
          result[summary.feature] += summary[predictedValueKey]
          result.isPrediction = true
        }
      }
    })

    return (result as unknown) as BarChartData & { timestamp: number }
  }

  const sortedData = widgetData
    .map(processEntry)
    .sort((a, b) => a.timestamp - b.timestamp)
    .map(({ timestamp: _, ...rest }) => rest)

  switch (groupingInterval) {
    case ActivityLogGroupingInterval.hourly: {
      const end = dayjs()

      const pastData = sortedData.filter((item) => {
        const itemStart = dayjs(item.startTimestamp)
        return itemStart.isBefore(end) || itemStart.isSame(end)
      })
      const futureData = sortedData.filter((item) => {
        const itemStart = dayjs(item.startTimestamp)
        return itemStart.isAfter(end)
      })

      const allHours = Array.from({ length: 24 }, (_, i) => end.subtract(23 - i, 'hours'))
      const dataMap = new Map(pastData.map((item) => [item.name, item]))

      const paddedData = allHours.map((hour) => {
        const hourLabel = hour.format('ha')
        return (
          dataMap.get(hourLabel) || {
            name: hourLabel,
            startTimestamp: hour.startOf('hour').format('YYYY-MM-DDTHH:mm:ssZ'),
            endTimestamp: hour.endOf('hour').format('YYYY-MM-DDTHH:mm:ssZ'),
            [ActivityEventType.create]: 0,
            [ActivityEventType.read]: 0,
            [ActivityEventType.write]: 0,
            [ActivityEventType.delete]: 0,
            [ActivityEventType.download]: 0,
            [ActivityEventType.uniqueRead]: 0,
            [ActivityEventType.uniqueWrite]: 0,
            [ActivityEventType.all]: 0
          }
        )
      })

      paddedData.push(...futureData)

      sortedData.length = 0
      sortedData.push(...paddedData)
      break
    }
    case ActivityLogGroupingInterval.daily: {
      const end = dayjs()
      const allDays = Array.from({ length: 7 }, (_, i) => end.subtract(6 - i, 'day'))
      const dataMap = new Map(sortedData.map((item) => [item.name, item]))

      const paddedData = allDays.map((day) => {
        const dayLabel = day.format('ddd')
        return (
          dataMap.get(dayLabel) || {
            name: dayLabel,
            startTimestamp: day.startOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
            endTimestamp: day.endOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
            [ActivityEventType.create]: 0,
            [ActivityEventType.read]: 0,
            [ActivityEventType.write]: 0,
            [ActivityEventType.delete]: 0,
            [ActivityEventType.download]: 0,
            [ActivityEventType.uniqueRead]: 0,
            [ActivityEventType.uniqueWrite]: 0,
            [ActivityEventType.all]: 0
          }
        )
      })

      const predictedEntries = sortedData.filter(
        (item) => item.isPrediction && dayjs(item.startTimestamp).isAfter(end)
      )
      paddedData.push(...predictedEntries)

      sortedData.length = 0
      sortedData.push(...paddedData)
      break
    }
    case ActivityLogGroupingInterval.weekly: {
      const end = dayjs()
      const numberOfWeeks = 4

      const pastData = sortedData.filter((item) => {
        const itemStart = dayjs(item.startTimestamp)
        return itemStart.isBefore(end) || itemStart.isSame(end)
      })
      const futureData = sortedData.filter((item) => {
        const itemStart = dayjs(item.startTimestamp)
        return itemStart.isAfter(end)
      })

      const allWeeks = Array.from({ length: numberOfWeeks }, (_, i) =>
        end.subtract(numberOfWeeks - 1 - i, 'week')
      )
      const dataMap = new Map(pastData.map((item) => [item.name, item]))

      const paddedData = allWeeks.map((week) => {
        const weekLabel = week.format('D MMM')
        return (
          dataMap.get(weekLabel) || {
            name: weekLabel,
            startTimestamp: week.startOf('week').format('YYYY-MM-DDTHH:mm:ssZ'),
            endTimestamp: week.endOf('week').format('YYYY-MM-DDTHH:mm:ssZ'),
            [ActivityEventType.create]: 0,
            [ActivityEventType.read]: 0,
            [ActivityEventType.write]: 0,
            [ActivityEventType.delete]: 0,
            [ActivityEventType.download]: 0,
            [ActivityEventType.uniqueRead]: 0,
            [ActivityEventType.uniqueWrite]: 0,
            [ActivityEventType.all]: 0
          }
        )
      })

      paddedData.push(...futureData)

      sortedData.length = 0
      sortedData.push(...paddedData)
      break
    }
  }

  switch (groupingInterval) {
    case ActivityLogGroupingInterval.hourly: {
      const end = dayjs()

      const pastData = sortedData.filter((item) => {
        const itemStart = dayjs(item.startTimestamp)
        return itemStart.isBefore(end) || itemStart.isSame(end)
      })
      const futureData = sortedData.filter((item) => {
        const itemStart = dayjs(item.startTimestamp)
        return itemStart.isAfter(end)
      })

      const allHours = Array.from({ length: 24 }, (_, i) => end.subtract(23 - i, 'hours'))
      const dataMap = new Map(pastData.map((item) => [item.name, item]))

      const paddedData = allHours.map((hour) => {
        const hourLabel = hour.format('ha')
        return (
          dataMap.get(hourLabel) || {
            name: hourLabel,
            startTimestamp: hour.startOf('hour').format('YYYY-MM-DDTHH:mm:ssZ'),
            endTimestamp: hour.endOf('hour').format('YYYY-MM-DDTHH:mm:ssZ'),
            [ActivityEventType.read]: 0,
            [ActivityEventType.write]: 0,
            [ActivityEventType.create]: 0,
            [ActivityEventType.delete]: 0,
            [ActivityEventType.download]: 0,
            [ActivityEventType.uniqueRead]: 0,
            [ActivityEventType.uniqueWrite]: 0,
            [ActivityEventType.all]: 0
          }
        )
      })

      paddedData.push(...futureData)

      sortedData.length = 0
      sortedData.push(...paddedData)
      break
    }
    case ActivityLogGroupingInterval.daily: {
      const end = dayjs()
      const allDays = Array.from({ length: 7 }, (_, i) => end.subtract(6 - i, 'day'))
      const dataMap = new Map(sortedData.map((item) => [item.name, item]))

      const paddedData = allDays.map((day) => {
        const dayLabel = day.format('ddd')
        return (
          dataMap.get(dayLabel) || {
            name: dayLabel,
            startTimestamp: day.startOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
            endTimestamp: day.endOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
            [ActivityEventType.create]: 0,
            [ActivityEventType.read]: 0,
            [ActivityEventType.write]: 0,
            [ActivityEventType.delete]: 0,
            [ActivityEventType.download]: 0,
            [ActivityEventType.uniqueRead]: 0,
            [ActivityEventType.uniqueWrite]: 0,
            [ActivityEventType.all]: 0
          }
        )
      })

      const predictedEntries = sortedData.filter(
        (item) => item.isPrediction && dayjs(item.startTimestamp).isAfter(end)
      )
      paddedData.push(...predictedEntries)

      sortedData.length = 0
      sortedData.push(...paddedData)
      break
    }
    case ActivityLogGroupingInterval.weekly: {
      const end = dayjs()
      const numberOfWeeks = 4

      const pastData = sortedData.filter((item) => {
        const itemStart = dayjs(item.startTimestamp)
        return itemStart.isBefore(end) || itemStart.isSame(end)
      })
      const futureData = sortedData.filter((item) => {
        const itemStart = dayjs(item.startTimestamp)
        return itemStart.isAfter(end)
      })

      const allWeeks = Array.from({ length: numberOfWeeks }, (_, i) =>
        end.subtract(numberOfWeeks - 1 - i, 'week')
      )

      const paddedData = allWeeks.map((week) => {
        const weekLabel = week.format('D MMM')
        // ? Match by proximity (≤3 days) to handle different week-start conventions
        // ? between the backend and the frontend slot generation
        const matchedItem = pastData.find(
          (item) => Math.abs(dayjs(item.startTimestamp).diff(week, 'day')) <= 3
        )
        return (
          matchedItem || {
            name: weekLabel,
            startTimestamp: week.startOf('week').format('YYYY-MM-DDTHH:mm:ssZ'),
            endTimestamp: week.endOf('week').format('YYYY-MM-DDTHH:mm:ssZ'),
            [ActivityEventType.read]: 0,
            [ActivityEventType.write]: 0,
            [ActivityEventType.delete]: 0,
            [ActivityEventType.create]: 0,
            [ActivityEventType.download]: 0,
            [ActivityEventType.uniqueRead]: 0,
            [ActivityEventType.uniqueWrite]: 0,
            [ActivityEventType.all]: 0
          }
        )
      })

      paddedData.push(...futureData)

      sortedData.length = 0
      sortedData.push(...paddedData)
      break
    }
  }

  if (selectedFeature === ActivityEventType.all) {
    return sortedData
  }

  return sortedData.map((entry) => ({
    name: entry.name,
    startTimestamp: entry.startTimestamp,
    endTimestamp: entry.endTimestamp,
    isPrediction: entry.isPrediction,
    [selectedFeature]: entry[selectedFeature] || 0
  })) as BarChartData[]
}
